import type {
  ConditionData,
  ConditionGroupData,
  ConditionGroupMap,
  ConditionMap,
  RuleData,
  RuleMap
} from "../components/RulesUI/Constraints/constraints.types";
import type { Optional } from "../forms/types/common";
import type { Rule, RuleType } from "@certa/studio-core";
import { studioState } from "../hooks/useStudioAPI";

/**
 * Converts rules provided by db into raw serialized rules
 *
 * INPUT
 * rules => ["01a2801e-1cf3-482e-aa08-8b882aeea413", "c7960370-8f5e-4a8e-a7bb-dc1137570452"]
 *
 * OUTPUT
 * rawRules => {
 *    "1": {
 *      "first_compare_field": "field_b2",
 *      "operator": "eq",
 *      "second_compare_field": "field_b1",
 *      "compare_group": 1,
 *      "depth": 0,
 *      "visibility": true,
 *      "for_value_required": true
 *    },
 *    "2": {
 *      "first_compare_field": "field_b1",
 *      "operator": "nt",
 *      "compare_value": "",
 *      "compare_group": 2,
 *      "depth": 1,
 *      "visibility": true,
 *      "for_value_required": true
 *    },
 *    ...
 *  }
 *
 * Commented out because not being used anywhere
 */
// export const ruleDBToRaw = async (
//   rules: string[],
//   ruleType: RuleType
// ): Promise<Record<string, Constraint>> => {
//   const rulesData = await Promise.all(
//     rules.map(async rule => {
//       const ruleData: Optional<RuleData, "groups" | "conditions"> | undefined =
//         await db.rules.get(rule);

//       if (ruleData) {
//         ruleData.conditions = {};
//         ruleData.groups = {};

//         let conditions: ConditionData[] = [];
//         let groups: ConditionGroupData[] = [];

//         const extractConditions = async (group: ConditionGroupData) => {
//           if (group.conditions && group.conditions.length > 0) {
//             const nestedConditions = await Promise.all(
//               group.conditions.map(
//                 async conditionId => await studioState().getCondition(conditionId)
//               )
//             );
//             nestedConditions.forEach(nestedCondition => {
//               if (nestedCondition) {
//                 conditions.push(nestedCondition);
//               }
//             });
//           }

//           if (group.groups && group.groups.length > 0) {
//             const nestedGroups = await Promise.all(
//               group.groups.map(
//                 async groupId => await studioState().getConditionGroup(groupId)
//               )
//             );
//             await Promise.all(
//               nestedGroups.map(async nestedGroup => {
//                 if (nestedGroup) {
//                   groups.push(nestedGroup);
//                   await extractConditions(nestedGroup);
//                 }
//               })
//             );
//           }
//         };

//         // Store top level group
//         let topLevelGroup;
//         if (ruleData.topLevelGroupId) {
//           topLevelGroup = await studioState().getConditionGroup(
//             ruleData.topLevelGroupId
//           );
//         }

//         if (topLevelGroup) {
//           groups.push(topLevelGroup);
//           await extractConditions(topLevelGroup);
//         }

//         // Remove conditions and groups that were not found
//         conditions = conditions.filter(condition => condition && condition.id);
//         groups = groups.filter(group => group && group.id);

//         conditions.forEach(condition => {
//           ruleData.conditions![condition.id] = condition;
//         });
//         groups.forEach(group => {
//           ruleData.groups![group.id] = group;
//         });

//         return ruleData as RuleData;
//       } else {
//         console.error(`Could not find rule with id ${rule} in db`);
//       }
//     })
//   );

//   const rulesDataMap: Record<string, RuleData> = {};
//   rulesData
//     .filter(ruleData => ruleData && ruleData.id)
//     .forEach(ruleData => {
//       if (ruleData) {
//         rulesDataMap[ruleData?.id] = ruleData;
//       }
//     });

//   const isConditionLessConstraint =
//     ruleType === RuleType.DataReduction ||
//     ruleType === RuleType.AttachmentInheritance;
//   const isConditionOptionalConstraint = ruleType === RuleType.Notification;

//   const rawRuleData = serialize(
//     rulesDataMap,
//     isConditionLessConstraint,
//     isConditionOptionalConstraint
//   );

//   return rawRuleData;
// };

export const ruleDBToMeta = (
  processId: string,
  rules: (Optional<RuleData, "groups" | "conditions"> | undefined)[] = []
): RuleMap => {
  const rulesData = rules.map(ruleData => {
    if (ruleData) {
      ruleData.conditions = {};
      ruleData.groups = {};

      let conditions: ConditionData[] = [];
      let groups: ConditionGroupData[] = [];

      const extractConditions = (group: ConditionGroupData) => {
        if (group.conditions && group.conditions.length > 0) {
          const nestedConditions = group.conditions.map(conditionId =>
            studioState().getCondition(processId, conditionId)
          );

          nestedConditions.forEach(nestedCondition => {
            if (nestedCondition) {
              conditions.push(nestedCondition);
            }
          });
        }

        if (group.groups && group.groups.length > 0) {
          const nestedGroups = group.groups.map(groupId =>
            studioState().getConditionGroup(processId, groupId)
          );

          nestedGroups.forEach(nestedGroup => {
            if (nestedGroup) {
              groups.push(nestedGroup);
              extractConditions(nestedGroup);
            }
          });
        }
      };

      // Store top level group
      let topLevelGroup;
      if (ruleData.topLevelGroupId) {
        topLevelGroup = studioState().getConditionGroup(
          processId,
          ruleData.topLevelGroupId
        );
      }

      if (topLevelGroup) {
        groups.push(topLevelGroup);
        extractConditions(topLevelGroup);
      }

      // Remove conditions and groups that were not found
      conditions = conditions.filter(condition => condition && condition.id);
      groups = groups.filter(group => group && group.id);

      conditions.forEach(condition => {
        ruleData.conditions![condition.id] = condition;
      });
      groups.forEach(group => {
        ruleData.groups![group.id] = group;
      });

      return ruleData as RuleData;
    } else {
      // Note: This can be a false positive error as well.
      // When Ruledata is undefined.
      // TODO: Find a way to address undefined case and a real error case separately.
      throw Error(`Unable to process the following ruledata ${ruleData}`);
    }
  });

  const rulesDataMap: Record<string, RuleData> = {};
  rulesData
    ?.filter(ruleData => ruleData && ruleData.id)
    ?.forEach(ruleData => {
      if (ruleData) {
        rulesDataMap[ruleData?.id] = ruleData;
      }
    });

  return rulesDataMap;
};

export const getRulesMapFromMeta = (rulesMeta: RuleMap, ruleType: RuleType) => {
  const newRuleMap: Record<string, Rule> = {};
  let aggregatedConditionMap: ConditionMap = {};
  let aggregatedConditionGroupMap: ConditionGroupMap = {};
  Object.values(rulesMeta).forEach(rule => {
    const { conditions, groups, ...otherProps } = rule;
    aggregatedConditionMap = { ...aggregatedConditionMap, ...conditions };
    aggregatedConditionGroupMap = { ...aggregatedConditionGroupMap, ...groups };

    newRuleMap[rule.id] = {
      ruleType,
      ...otherProps
    };
  });

  return {
    ruleMap: newRuleMap,
    conditionMap: aggregatedConditionMap,
    conditionGroupMap: aggregatedConditionGroupMap
  };
};
